import { Component, Event, EventEmitter, getElement, h, Host, Listen, Method, Prop, State } from '@stencil/core';
import type { Entry } from '../../../types/site';
import { locationPathIsNotHome } from '../../../utils/router-utils';

@Component({
  tag: 'sy-site-sidebar',
  styleUrl: './scss/index.scss',
  shadow: true,
  assetsDirs: ['assets'],
})
export class SydleSiteSidebar {
  private sidebar: HTMLSySidebarElement;

  /**
   * List of menu entries
   */
  @Prop() entries: Entry[];

  /**
   * Active icon
   */
  @State() activeIcon: string;

  /**
   * Use to add path alternates urls
   */
  @Prop() appsPaths: string;

  /**
   * Event Emitted when sidebar shows up
   */
  @Event({ bubbles: true, composed: true }) sySiteSidebarDidShow: EventEmitter<void>;

  /**
   * Event emitted when sidebar hide
   */
  @Event({ bubbles: true, composed: true }) sySiteSidebarDidHide: EventEmitter<void>;

  /**
   * Method to show sidebar
   */
  @Method()
  async show(): Promise<void> {
    const sidebar = this.sidebar;
    this.sidebar.show();
    sidebar.classList.add('open');
    sidebar.classList.remove('closed');
  }

  /**
   * Method to hide sidebar
   */
  @Method()
  async hide(): Promise<void> {
    const sidebar = this.sidebar;
    sidebar.classList.add('closed');
    sidebar.classList.remove('open');
  }

  /**
   * Method to toggle sidebar visibility
   */
  @Method()
  async toggle(): Promise<void> {
    this.sidebar.toggle();
  }

  /**
   * Shifts sidebar to given gap
   */
  @Method()
  async shift(gap: string): Promise<void> {
    this.sidebar.style.top = gap + 'px';
  }

  @Listen('sySidebarDidShow')
  emitShowEvent(): void {
    this.sySiteSidebarDidShow.emit();
  }

  @Listen('sySidebarDidHide')
  emitHideEvent(): void {
    this.sySiteSidebarDidHide.emit();
  }

  componentWillRender() {
    if (locationPathIsNotHome()) {
      this.activeIcon =
        this.entries?.[0]?.children?.find((icon) => icon.href?.includes(window.location.pathname))?.title || '';
    }
  }

  private sidebarCloseHandler(event: Event) {
    event.stopPropagation();
    const header = (getElement(this)?.parentNode as ShadowRoot)?.host as HTMLSySiteHeaderElement;
    header?.sidebarClosed();
  }

  componentDidRender() {
    const sidebar = this.sidebar as HTMLSySidebarElement;
    const backdrop = sidebar.shadowRoot?.querySelector('sy-backdrop');
    if (!backdrop) {
      return;
    }

    backdrop.innerHTML = `<div style="width:100%; height:100%;"></div>`;
    const div = sidebar.shadowRoot?.querySelector('sy-backdrop div');
    div?.addEventListener('click', (event: Event) => this.sidebarCloseHandler(event));
  }

  @Listen('keydown', { target: 'body' })
  escapeKeyHandler(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.sidebar.visible) {
      this.sidebarCloseHandler(event);
    }
  }

  private handleAnimation(event: AnimationEvent): void {
    if (event.animationName === 'slide-left') {
      const sidebar = this.sidebar;
      sidebar.hide();
    }
  }

  render() {
    return (
      <Host>
        <sy-sidebar ref={(el) => (this.sidebar = el)} onanimationend={(e: AnimationEvent) => this.handleAnimation(e)}>
          <div class="container">
            <sy-site-sidebar-link-grid
              category={this.entries?.[0].title.toUpperCase()}
              icons={this.entries?.[0].children}
              activeIcon={this.activeIcon}
            ></sy-site-sidebar-link-grid>
            <ul>
              {this.entries?.slice(1).map((link) => {
                return (
                  <li key={link.title}>
                    {link.children && link.children.length > 0 ? (
                      <sy-site-sidebar-list-collapse entry={link}></sy-site-sidebar-list-collapse>
                    ) : (
                      <a
                        target="_self"
                        href={link.href}
                        class={{
                          link: true,
                          active: locationPathIsNotHome() && link.href?.includes(window.location.pathname),
                        }}
                      >
                        <sy-text type="small">{link.title}</sy-text>
                      </a>
                    )}
                  </li>
                );
              })}
            </ul>
            <sy-site-language-buttons type="circle" appsPaths={this.appsPaths}></sy-site-language-buttons>
          </div>
        </sy-sidebar>
      </Host>
    );
  }
}
